<script lang="ts">
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import SaveStatus from "$components/SaveStatus.svelte";
  import { SaveTracker } from "$lib/saveTracker.svelte";
  import { api, requestUpgrade } from "$lib/api";
  import { auth } from "$lib/auth";
  import { canAction, hasEntitlement } from "$lib/permissions";
  import { fetchCategories, type Category } from "$lib/catalog";
  import PriceMatrix, { type FlatRow, type Target } from "./PriceMatrix.svelte";

  let {
    params,
    tenantSlug: tenantSlugProp = "",
    embedded = false,
    noteHidden = false,
    onHideNote = undefined,
  }: {
    params?: { slug: string };
    tenantSlug?: string;
    embedded?: boolean;
    /** The visibility note was put away into the «?» beside the page title. */
    noteHidden?: boolean;
    onHideNote?: () => void;
  } = $props();
  const slug = $derived(tenantSlugProp || params?.slug || "");

  type PriceRow = FlatRow & { link_id: number; currency: string };
  type LinkInfo = {
    id: number;
    buyer_tenant: { slug: string; display_name: string; country?: string } | null;
    seller_tenant: { slug: string; display_name: string } | null;
  };

  let rawRows = $state<any[]>([]);
  let links = $state<LinkInfo[]>([]);
  let categories = $state<Category[]>([]);
  let loading = $state(true);

  function partnerName(link: LinkInfo): string {
    return link.buyer_tenant?.display_name || `Link #${link.id}`;
  }

  const targets = $derived<Target[]>(
    links.map((l) => ({ id: l.id, name: partnerName(l), country: l.buyer_tenant?.country ?? "" })),
  );
  const flatRows = $derived<PriceRow[]>(
    rawRows.map((r) => ({ ...r, target_id: r.link_id })),
  );

  /* `silent` refreshes without flipping `loading`, which unmounts the matrix
     (and its scroll position). Used for every post-mutation refresh. */
  async function load(silent = false) {
    if (!silent) loading = true;
    try {
      const [priceData, linkData, cats] = await Promise.all([
        api.get<any[]>(`/t/${slug}/pricing/distributor-prices`),
        api.get<LinkInfo[]>(`/t/${slug}/supplier-links`),
        fetchCategories(slug).catch(() => [] as Category[]),
      ]);
      rawRows = priceData;
      // Only links where we are the seller: the columns are *our* buyers.
      // The endpoint also returns links where we are the buying side — that
      // used to leak our own company in as a phantom, uneditable column.
      links = linkData.filter((l) => l.seller_tenant?.slug === slug);
      categories = cats;
    } catch (e) { console.error(e); }
    finally { loading = false; }
  }

  // Every write runs through the tracker (OE-A21): visible saving/saved
  // state, retry on failure, and a short undo restoring the previous value.
  const saver = new SaveTracker();

  function rowFor(productId: number, linkId?: number) {
    return rawRows.find(
      (r) => r.product_id === productId && (linkId === undefined || r.link_id === linkId),
    );
  }

  async function onSavePrice(productId: number, linkId: number, value: string) {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    const prev = rowFor(productId, linkId)?.price ?? null;
    const set = async (price: number) => {
      await api.patch(`/t/${slug}/pricing/distributor-prices/${linkId}/${productId}`, {
        price,
        currency: "EUR",
      });
      await load(true);
    };
    await saver.run(
      () => set(num),
      prev != null ? () => set(Number(prev)) : undefined,
    );
  }

  async function onSaveBase(productId: number, value: string) {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    const prev = rowFor(productId)?.base_price ?? null;
    const set = async (base: number) => {
      await api.patch(`/t/${slug}/pricing/base-price/${productId}`, { base_price: base });
      await load(true);
    };
    await saver.run(() => set(num), prev != null ? () => set(Number(prev)) : undefined);
  }

  /* ── Access (from the «Доступ позиций» page, merged here 2026-09-16) ────
     Opening a product to a client publishes the catalogue, so it asks for the
     seller entitlement; closing never does. Undo restores each row's own
     previous value — a mixed column comes back mixed. */
  const canManageAccess = $derived(canAction($auth, slug, "assortment.manage", ["pricing", "edit"]));
  function mayOpen(opening: boolean): boolean {
    if (opening && !hasEntitlement($auth, slug, "publish_catalog")) {
      requestUpgrade("publish_catalog");
      return false;
    }
    return true;
  }
  type AccessField = "is_accessible" | "is_exclusive";
  const ACCESS_PATH: Record<AccessField, string> = {
    is_accessible: "distributor-accessible",
    is_exclusive: "distributor-exclusive",
  };
  async function setOne(field: AccessField, linkId: number, productId: number, value: boolean) {
    await api.patch(`/t/${slug}/pricing/${ACCESS_PATH[field]}/${linkId}/${productId}`, { [field]: value });
    await load(true);
  }
  async function setMany(field: AccessField, linkId: number, productIds: number[], value: boolean) {
    if (productIds.length === 0) return;
    await api.post(`/t/${slug}/pricing/${ACCESS_PATH[field]}-bulk/${linkId}`, {
      product_ids: productIds,
      [field]: value,
    });
  }
  async function toggleOne(field: AccessField, productId: number, linkId: number, value: boolean) {
    if (!canManageAccess || !mayOpen(value)) return;
    await saver.run(
      () => setOne(field, linkId, productId, value),
      () => setOne(field, linkId, productId, !value),
    );
  }
  async function toggleMany(field: AccessField, linkId: number, productIds: number[], value: boolean) {
    if (!canManageAccess || !mayOpen(value)) return;
    const prev = productIds.map((pid) => ({ pid, on: !!rowFor(pid, linkId)?.[field] }));
    await saver.run(
      async () => {
        await setMany(field, linkId, productIds, value);
        await load(true);
      },
      async () => {
        for (const on of [true, false]) {
          await setMany(field, linkId, prev.filter((p) => p.on === on).map((p) => p.pid), on);
        }
        await load(true);
      },
    );
  }

  // Header checkbox in a client's column: copy the Base price onto that client
  // for every product in the filtered view that is OPEN to them (other clients
  // and closed rows stay untouched — access is the checkbox beside the price).
  async function onBaseToTarget(linkId: number, productIds: number[]) {
    const prev = productIds
      .map((pid) => ({ pid, price: rowFor(pid, linkId)?.price }))
      .filter((p) => p.price != null);
    await saver.run(
      async () => {
        await api.post(`/t/${slug}/pricing/base-to-target-bulk/${linkId}`, {
          product_ids: productIds,
        });
        await load(true);
      },
      prev.length
        ? async () => {
            for (const p of prev) {
              await api.patch(`/t/${slug}/pricing/distributor-prices/${linkId}/${p.pid}`, {
                price: Number(p.price),
                currency: "EUR",
              });
            }
            await load(true);
          }
        : undefined,
    );
  }

  // Price formation: purchase price + markup coefficient in one write, plus
  // the recomputed base sale price when it moved.
  async function onFormation(
    productId: number,
    data: { cost_price: number; markup_pct: number; base_price: number | null },
  ) {
    const row = rowFor(productId);
    const prev =
      row && row.cost_price != null
        ? {
            cost_price: Number(row.cost_price),
            markup_pct: Number(row.markup_pct ?? 0),
            base_price: row.base_price != null ? Number(row.base_price) : null,
          }
        : null;
    const set = async (d: { cost_price: number; markup_pct: number; base_price: number | null }) => {
      await api.patch(`/t/${slug}/pricing/costs/${productId}`, {
        cost_price: d.cost_price,
        markup_pct: d.markup_pct,
      });
      if (d.base_price != null) {
        await api.patch(`/t/${slug}/pricing/base-price/${productId}`, { base_price: d.base_price });
      }
      await load(true);
    };
    await saver.run(() => set(data), prev ? () => set(prev) : undefined);
  }



  onMount(() => { if (slug) load(); });
</script>

{#snippet content()}
  <div class="dp-page" class:dp-embedded={embedded}>
    {#if !embedded}
      <div class="dp-header">
        <h1 class="dp-title">{$_("nav.pricingDistributorPrices")}</h1>
        <p class="dp-desc">{$_("prices.distributors.desc")}</p>
      </div>
    {/if}

    <PriceMatrix
      tenantSlug={slug}
      {loading}
      {flatRows}
      {targets}
      {categories}
      emptyText={$_("prices.distributors.empty")}
      infoNote={noteHidden ? "" : $_("prices.visibility.note")}
      {onHideNote}
      {onSaveBase}
      {onSavePrice}
      {onBaseToTarget}
      {onFormation}
      accessEditable={canManageAccess}
      onToggleAccessible={(pid, lid, v) => toggleOne("is_accessible", pid, lid, v)}
      onBulkAccessible={(lid, ids, v) => toggleMany("is_accessible", lid, ids, v)}
      onToggleExclusive={(pid, lid, v) => toggleOne("is_exclusive", pid, lid, v)}
      onBulkExclusive={(lid, ids, v) => toggleMany("is_exclusive", lid, ids, v)}
    />
  </div>
  <SaveStatus tracker={saver} />
{/snippet}

{#if embedded}
  {@render content()}
{:else}
  <AppShell tenantSlug={slug} active="pricing/distributor-prices">
    {@render content()}
  </AppShell>
{/if}

<style>
  .dp-page { display: flex; flex-direction: column; gap: 20px; }
  .dp-embedded { gap: 0; }
  .dp-header h1 { margin: 0; font-size: 22px; font-weight: 700; }
  .dp-desc { margin: 4px 0 0; font-size: 14px; color: var(--color-text-muted); }
</style>
